import React, { useEffect, useMemo, useState } from 'react';
/**
 * OrderingRenderer — Drag-to-reorder items into correct sequence.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s68.
 * Mobile: Uses framer-motion Reorder with native touch support.
 * touch-action: none prevents page scroll during drag.
 */
import { Reorder } from 'framer-motion';
import { QuestionRendererProps } from './types';
import { GripVertical } from 'lucide-react';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
import { shuffleWithSeed } from '../../../utils/deterministicShuffle';
import { hapticManager } from '../../../utils/hapticManager';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
interface OrderItem {
  id: number;
  content: string;
}
/**
 * Deterministic shuffle ensuring result differs from correctOrder.
 *
 * Sprint R2 F6: replaces Math.random()-based shuffle with seeded shuffle
 * keyed on question.id, so session-recovery and re-mount produce the same
 * starting order. Re-roll uses an attempt-suffixed seed when the first
 * shuffle accidentally matches correctOrder.
 */
function shuffleAwayFromCorrect(items: OrderItem[], correctOrder: number[], seed: string): OrderItem[] {
  if (items.length <= 1) return [...items];
  for (let attempt = 0; attempt < 20; attempt++) {
    const {
      shuffled
    } = shuffleWithSeed(items, attempt === 0 ? seed : `${seed}-attempt-${attempt}`);
    const ids = shuffled.map(item => item.id);
    const matches = correctOrder.length > 0 && ids.length === correctOrder.length && ids.every((id, idx) => id === correctOrder[idx]);
    if (!matches) return shuffled;
  }
  return [...items].reverse();
}
export function OrderingRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = question.data || {};
  const orderingData = config.ordering || config;
  const items: string[] = orderingData.items || [];
  const correctOrder: number[] = orderingData.correctOrder || [];
  const [hasInteracted, setHasInteracted] = useState(false);
  // STU UX Audit P1 (CC-3): tap-to-swap as primary touch interaction.
  // First tap selects an item; second tap on a different item swaps
  // their positions. Drag remains as a bonus for users who can use it.
  const [selectedForSwap, setSelectedForSwap] = useState<number | null>(null);
  const [order, setOrder] = useState(() => {
    const mapped = items.map((item, index) => ({
      id: index,
      content: item
    }));
    return shuffleAwayFromCorrect(mapped, correctOrder, question.id);
  });
  useEffect(() => {
    const mapped = items.map((item, index) => ({
      id: index,
      content: item
    }));
    setOrder(shuffleAwayFromCorrect(mapped, correctOrder, question.id));
    setHasInteracted(false);
    setSelectedForSwap(null);
  }, [question.id]);
  useEffect(() => {
    onReadyChange?.(hasInteracted);
  }, [hasInteracted, onReadyChange, question.id]);
  // REN-1 R-03: migrated to useEdgeTriggeredSubmit (removes stale-closure
  // eslint-disable from R-07 — hook tracks `value` in deps natively).
  const answerPayload = useMemo(() => order.map(item => item.id).join(','), [order]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  // Guard: no items — rendered AFTER all hooks
  if (!Array.isArray(items) || items.length === 0) {
    return <div className="text-center p-8 bg-amber-50 rounded-xl border-2 border-amber-200" data-id="element-10673">
        <p className="text-amber-700 font-bold" data-id="element-10674">⚠️ Geen items beschikbaar</p>
        <p className="text-sm text-amber-600 mt-2" data-id="element-10675">
          Deze vraag heeft geen geldige volgordedata.
        </p>
      </div>;
  }
  const handleReorder = (newOrder: OrderItem[]) => {
    setOrder(newOrder);
    setSelectedForSwap(null);
    if (!hasInteracted) setHasInteracted(true);
  };
  const moveItem = (fromIndex: number, direction: -1 | 1) => {
    if (shouldValidate) return;
    const toIndex = fromIndex + direction;
    if (toIndex < 0 || toIndex >= order.length) return;
    const next = [...order];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setOrder(next);
    if (!hasInteracted) setHasInteracted(true);
    // F-FEEL-2 follow-up: tactile feel on button-driven reorder
    hapticManager.hapticLight();
  };
  // STU UX Audit P1: tap-to-swap handler. First tap selects, second
  // tap on different item swaps positions.
  const handleItemTap = (index: number) => {
    if (shouldValidate) return;
    if (selectedForSwap === null) {
      setSelectedForSwap(index);
      hapticManager.hapticSelection();
      return;
    }
    if (selectedForSwap === index) {
      // tap same item again = deselect
      setSelectedForSwap(null);
      return;
    }
    // swap positions
    const next = [...order];
    [next[selectedForSwap], next[index]] = [next[index], next[selectedForSwap]];
    setOrder(next);
    setSelectedForSwap(null);
    if (!hasInteracted) setHasInteracted(true);
    hapticManager.hapticLight();
  };
  const handleItemKeyDown = (e: React.KeyboardEvent<HTMLLIElement>, index: number) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveItem(index, -1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveItem(index, 1);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleItemTap(index);
    }
  };
  return <div className="w-full max-w-lg mx-auto" data-id="element-10676">
      <p className="text-center text-slate-500 text-sm mb-4" data-id="element-10677">
        <span className="hidden md:inline" data-id="element-10678">
          Sleep de blokken in de juiste volgorde — of gebruik de pijltjes /
          toetsen
        </span>
        <span className="md:hidden" data-id="element-10679">
          Tik op twee blokken om ze om te wisselen — of houd ingedrukt en sleep
        </span>
      </p>
      <Reorder.Group axis="y" values={order} onReorder={handleReorder} className="space-y-3" style={{
      touchAction: 'pan-y'
    }} role="listbox" aria-label="Items om te ordenen. Tik op twee items om ze om te wisselen, of gebruik pijltje omhoog/omlaag om de geselecteerde regel te verplaatsen." data-id="element-10680">
        {order.map((item, index) => {
        const isSelectedForSwap = selectedForSwap === index;
        return <Reorder.Item key={item.id} value={item} className={`bg-white border-2 rounded-xl p-4 flex items-center gap-3 shadow-sm cursor-grab active:cursor-grabbing select-none focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-300 focus-visible:border-indigo-400 transition-colors ${isSelectedForSwap ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-300' : 'border-slate-200'}`} style={{
          touchAction: 'pan-y'
        }} whileDrag={{
          scale: 1.03,
          boxShadow: '0 8px 25px rgba(99, 102, 241, 0.2)',
          borderColor: 'rgb(129, 140, 248)',
          zIndex: 50
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 25
        }} tabIndex={shouldValidate ? -1 : 0} role="option" aria-selected={isSelectedForSwap} aria-label={`Positie ${index + 1} van ${order.length}: ${item.content}${isSelectedForSwap ? ' — geselecteerd, tik op een ander blok om te wisselen' : ''}`} onKeyDown={e => handleItemKeyDown(e, index)} onClick={() => handleItemTap(index)} onDragStart={() => {
          setSelectedForSwap(null);
          hapticManager.hapticSelection();
        }} onDragEnd={() => hapticManager.hapticLight()} data-id="element-10681">
              <div className="flex-shrink-0 w-8 h-8 md:w-6 md:h-6 flex items-center justify-center rounded-lg bg-slate-100 text-slate-400" data-id="element-10682">
                <GripVertical className="w-5 h-5 md:w-4 md:h-4" data-id="element-10683" />
              </div>
              <span className="text-lg font-medium text-slate-700 flex-1 line-clamp-2" data-id="element-10684">
                <LatexRenderer content={item.content} data-id="element-10685" />
              </span>
              <div className="flex items-center gap-1" data-id="element-10686">
                <button type="button" onClick={e => {
              e.stopPropagation();
              moveItem(index, -1);
            }} disabled={shouldValidate || index === 0} aria-label={`Verplaats "${item.content}" naar boven`} className="w-11 h-11 md:w-8 md:h-8 flex items-center justify-center rounded-md text-slate-500 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500" data-id="element-10687">
                  <span aria-hidden="true" data-id="element-10688">▲</span>
                </button>
                <button type="button" onClick={e => {
              e.stopPropagation();
              moveItem(index, 1);
            }} disabled={shouldValidate || index === order.length - 1} aria-label={`Verplaats "${item.content}" naar beneden`} className="w-11 h-11 md:w-8 md:h-8 flex items-center justify-center rounded-md text-slate-500 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500" data-id="element-10689">
                  <span aria-hidden="true" data-id="element-10690">▼</span>
                </button>
              </div>
              <span className="text-xs font-bold text-slate-300 tabular-nums w-5 text-right" data-id="element-10691">
                {index + 1}
              </span>
            </Reorder.Item>;
      })}
      </Reorder.Group>
    </div>;
}